/**
 * Take Quiz Page
 * Interactive quiz-taking interface with question navigation
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Slider } from '@voilajsx/uikit/slider';
import { Loader2, ChevronLeft, ChevronRight, CheckCircle, Check } from 'lucide-react';
import { ProgressBar } from '../../components';
import { useQuiz } from '../../context/QuizContext';
import { SEO, Header, Footer } from '../../../../shared/components';
import type { Question } from '../../types';

const TakeQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    currentQuizType,
    questions,
    answers,
    currentQuestionIndex,
    isLoading,
    error,
    answerQuestion,
    goToNextQuestion,
    goToPreviousQuestion,
    submitQuiz,
    startQuiz,
  } = useQuiz();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top when quiz starts or question changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (id && !currentQuizType) {
      startQuiz(parseInt(id));
    }
  }, [id, currentQuizType, startQuiz]);

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers.find(a => a.questionId === currentQuestion?.id);
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const canGoNext = currentAnswer !== undefined || !currentQuestion?.is_required;

  const handleAnswerChange = (value: any) => {
    if (currentQuestion) {
      answerQuestion(currentQuestion.id, value);
    }
  };

  const handleNext = () => {
    if (!isLastQuestion) {
      goToNextQuestion();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      goToPreviousQuestion();
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await submitQuiz();
      navigate('/quiz/results');
    } catch (err: any) {
      console.error('Submit error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestionInput = (question: Question) => {
    const value = currentAnswer?.value;
    const options = question.options || [];

    switch (question.question_type) {
      // Single choice - Use dropdown for 5+ options, radio buttons for fewer
      case 'single':
        if (options.length >= 5) {
          // Dropdown for many options
          return (
            <div className="space-y-3">
              <Select value={value as string} onValueChange={handleAnswerChange}>
                <SelectTrigger className="w-full h-14 text-base border-2 border-border rounded-xl focus:border-primary">
                  <SelectValue placeholder="Select an option..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {options.map((option, index) => (
                    <SelectItem key={index} value={option.value} className="text-base py-3">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        } else {
          // Radio cards for fewer options - 2x2 grid
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {options.map((option, index) => (
                <label
                  key={index}
                  className={`group flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    value === option.value
                      ? 'border-primary bg-gradient-to-r from-primary/10 to-emerald-500/5 shadow-md'
                      : 'border-border hover:border-primary/50 hover:shadow-sm'
                  }`}
                >
                  <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all flex-shrink-0 ${
                    value === option.value
                      ? 'border-primary bg-primary'
                      : 'border-border group-hover:border-primary'
                  }`}>
                    {value === option.value && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                  <span className={`ml-3 text-sm font-medium ${
                    value === option.value ? 'text-primary' : 'text-foreground'
                  }`}>
                    {option.label}
                  </span>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleAnswerChange(e.target.value)}
                    className="sr-only"
                  />
                </label>
              ))}
            </div>
          );
        }

      // Multiple choice - Checkbox cards in 2x2 grid
      case 'multiple':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {options.map((option, index) => {
              const selectedValues = (value as string[]) || [];
              const isChecked = selectedValues.includes(option.value);

              return (
                <label
                  key={index}
                  className={`group flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    isChecked
                      ? 'border-primary bg-gradient-to-r from-primary/10 to-emerald-500/5 shadow-md'
                      : 'border-border hover:border-primary/50 hover:shadow-sm'
                  }`}
                >
                  <div className={`flex items-center justify-center w-5 h-5 rounded-md border-2 transition-all flex-shrink-0 ${
                    isChecked
                      ? 'border-primary bg-primary'
                      : 'border-border group-hover:border-primary'
                  }`}>
                    {isChecked && (
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    )}
                  </div>
                  <span className={`ml-3 text-sm font-medium ${
                    isChecked ? 'text-primary' : 'text-foreground'
                  }`}>
                    {option.label}
                  </span>
                  <input
                    type="checkbox"
                    value={option.value}
                    checked={isChecked}
                    onChange={(e) => {
                      const newValues = isChecked
                        ? selectedValues.filter(v => v !== option.value)
                        : [...selectedValues, option.value];
                      handleAnswerChange(newValues);
                    }}
                    className="sr-only"
                  />
                </label>
              );
            })}
          </div>
        );

      // Range slider for number ranges
      case 'range':
        const numValue = (value as number) || 0;
        const min = question.options?.[0]?.value ? parseInt(question.options[0].value) : 0;
        const max = question.options?.[1]?.value ? parseInt(question.options[1].value) : 100;
        const step = question.options?.[2]?.value ? parseInt(question.options[2].value) : 1;

        return (
          <div className="space-y-6">
            <div className="px-2">
              <Slider
                value={[numValue]}
                onValueChange={(values) => handleAnswerChange(values[0])}
                min={min}
                max={max}
                step={step}
                className="w-full"
              />
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground px-2">
              <span>{min}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide">Selected:</span>
                <span className="text-2xl font-bold text-primary">{numValue}</span>
              </div>
              <span>{max}</span>
            </div>
          </div>
        );

      // Number input with modern styling
      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              value={value as number || ''}
              onChange={(e) => handleAnswerChange(parseInt(e.target.value) || 0)}
              className="w-full px-6 py-4 text-lg font-semibold border-2 border-border rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              placeholder="Enter a number"
            />
          </div>
        );

      // Text input - Not recommended, but available as fallback
      case 'text':
      default:
        return (
          <div className="relative">
            <input
              type="text"
              value={value as string || ''}
              onChange={(e) => handleAnswerChange(e.target.value)}
              className="w-full px-6 py-4 text-base border-2 border-border rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              placeholder="Type your answer"
            />
          </div>
        );
    }
  };

  if (isLoading || !currentQuestion) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading quiz...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => navigate('/quiz')}>
              Back to Quizzes
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${currentQuizType?.name} - Question ${currentQuestionIndex + 1}`}
        description={`Take the ${currentQuizType?.name} quiz`}
      />

      <Header />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-6 py-12 max-w-3xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {currentQuizType?.name}
            </h1>
            <ProgressBar
              current={currentQuestionIndex + 1}
              total={questions.length}
            />
          </div>

          {/* Question Card */}
          <div className="bg-card border border-border rounded-2xl p-8 mb-6 shadow-sm">
            <div className="mb-6">
              {currentQuestion.icon_emoji && (
                <span className="text-4xl mb-4 block">{currentQuestion.icon_emoji}</span>
              )}
              <h2 className="text-xl font-semibold text-foreground mb-2">
                {currentQuestion.question_text}
              </h2>
              {currentQuestion.help_text && (
                <p className="text-sm text-muted-foreground">
                  {currentQuestion.help_text}
                </p>
              )}
            </div>

            {renderQuestionInput(currentQuestion)}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !canGoNext}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Submit Quiz
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canGoNext}
              >
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Answered Questions Indicator */}
          <div className="mt-6 flex justify-center gap-2">
            {questions.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full ${
                  answers.find(a => a.questionId === questions[idx].id)
                    ? 'bg-primary'
                    : idx === currentQuestionIndex
                    ? 'bg-primary/50'
                    : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default TakeQuizPage;
